import { useEffect, useState } from 'react';
import { eventTarget } from '@cornerstonejs/core';
import { annotation, Enums as ToolEnums } from '@cornerstonejs/tools';

/** 'ai' when ANY annotation on the frame is AI-sourced; 'manual' when every
 *  annotation on it is the reader's. */
export type AnnotatedFrameKind = 'ai' | 'manual';

export interface AnnotatedFrame {
  /** Zero-based frame index (scrubber step). */
  frame: number;
  kind: AnnotatedFrameKind;
}

const RECOMPUTE_EVENTS = [
  ToolEnums.Events.ANNOTATION_ADDED,
  ToolEnums.Events.ANNOTATION_COMPLETED,
  ToolEnums.Events.ANNOTATION_MODIFIED,
  ToolEnums.Events.ANNOTATION_REMOVED,
];

function readAnnotatedFrames(dicomUuid: string): AnnotatedFrame[] {
  const prefix = `video:${dicomUuid}/frames/`;
  const byFrame = new Map<number, AnnotatedFrameKind>();

  annotation.state.getAllAnnotations().forEach((a) => {
    const refImageId = (
      a.metadata as { referencedImageId?: string } | undefined
    )?.referencedImageId;
    if (!refImageId?.startsWith(prefix)) return;
    if (
      a.annotationUID &&
      !annotation.visibility.isAnnotationVisible(a.annotationUID)
    ) {
      return;
    }
    // Video imageIds are 1-based (`…/frames/<n>`); scrubber steps are 0-based.
    const frame = Number(refImageId.slice(prefix.length)) - 1;
    if (!Number.isInteger(frame) || frame < 0) return;

    const isAI = (a.data as { isAI?: boolean } | undefined)?.isAI === true;
    if (isAI) byFrame.set(frame, 'ai');
    else if (!byFrame.has(frame)) byFrame.set(frame, 'manual');
  });

  return [...byFrame.entries()]
    .map(([frame, kind]) => ({ frame, kind }))
    .sort((a, b) => a.frame - b.frame);
}

/**
 * Frames of this dicom that carry an annotation, read from Cornerstone's LIVE
 * annotation state — i.e. exactly what is drawn on the image. Hydration has
 * already applied the worksheet's rules there (human supersedes AI,
 * soft-deleted AI is never drawn, only the ranked variant, only rows the
 * worksheet defines), and live edits mutate it in place: adopting an AI line
 * flips its `isAI` flag, deleting removes it, drawing adds one. Reading the
 * saved report instead lags all of those until the next save.
 */
export function useAnnotatedFrames(
  dicomUuid: string | undefined
): AnnotatedFrame[] {
  const [frames, setFrames] = useState<AnnotatedFrame[]>([]);

  useEffect(() => {
    if (!dicomUuid) {
      setFrames([]);
      return undefined;
    }

    let signature = '';
    const recompute = () => {
      const next = readAnnotatedFrames(dicomUuid);
      // ANNOTATION_MODIFIED fires continuously during a drag — only re-render
      // when the marker set actually changes.
      const nextSignature = next.map((f) => `${f.frame}${f.kind}`).join('|');
      if (nextSignature === signature) return;
      signature = nextSignature;
      setFrames(next);
    };

    // AI adoption flips `isAI` on mouse-up without always emitting an event,
    // so re-read once the mouse-up handlers have run.
    const onMouseUp = () => requestAnimationFrame(recompute);

    recompute();
    RECOMPUTE_EVENTS.forEach((evt) =>
      eventTarget.addEventListener(evt, recompute)
    );
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      RECOMPUTE_EVENTS.forEach((evt) =>
        eventTarget.removeEventListener(evt, recompute)
      );
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [dicomUuid]);

  return frames;
}
